import { resolveLocalized } from '@fixit/bozp-renderer';
import { useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { getTraining } from '@/api/training';
import { Masthead } from '@/components/Masthead';
import { ReturnButton } from '@/components/ReturnButton';
import { useT } from '@/i18n';
import { useLocale } from '@/i18n/locale';
import { isEmbedded, postToParent } from '@/lib/embed';
import { getLastResult } from '@/result-store';
import { getSession, type PlayerSession } from '@/session';
import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/training/$hazardId/result',
  component: ResultPage,
});

/**
 * Should the pass screen offer "Back to overview" alongside the return/close
 * action?
 *
 * Only when the hub still leads somewhere. With nothing left to complete the
 * two buttons were near-identical dead ends stacked on top of each other -
 * the tester read them as "hotovo / dokončit, both the same, neither works"
 * (2026-07-25). The exception is a standalone browser session with no return
 * deep link: there the hub button is the only way out, so it stays.
 */
export function shouldShowHubButton({
  trainings,
  embed,
  returnUrl,
}: Pick<PlayerSession, 'trainings' | 'embed' | 'returnUrl'>): boolean {
  const hasPendingTrainings = trainings.some((tr) => tr.status !== 'valid');
  const hasReturnAffordance = isEmbedded(embed) || !!returnUrl;
  return hasPendingTrainings || !hasReturnAffordance;
}

function ResultPage() {
  const t = useT();
  const LOCALE = useLocale();
  const navigate = useNavigate();
  const { hazardId } = useParams({ from: '/training/$hazardId/result' });
  const result = getLastResult();
  const session = getSession();
  const { returnUrl, embed, parentOrigin } = session;
  // The quiz route already called `markTrainingValid` for a pass, so the
  // session's statuses are the live "anything left to do?" signal.
  const showHubButton = shouldShowHubButton(session);

  // Cached from the LMS/quiz route - used to map weak chapter ids → human titles.
  const { data: training } = useQuery({
    queryKey: ['training', hazardId],
    queryFn: () => getTraining(hazardId),
  });
  const titleFor = (id: string) => {
    const ch = training?.content.chapters.find((c) => c.id === id);
    return ch ? resolveLocalized(ch.title, LOCALE) : id;
  };

  // Guard: no result in memory (e.g. a refresh) → back to hub.
  useEffect(() => {
    if (!result || result.hazardProfileId !== hazardId) {
      navigate({ to: '/hub' });
    }
  }, [result, hazardId, navigate]);

  // Embed handshake: tell the framing web account that this training PASSED, exactly once.
  // Fires only on a genuine pass - never on fail - and no-ops outside the allowlisted web embed.
  const completedPosted = useRef(false);
  useEffect(() => {
    if (completedPosted.current) return;
    if (!result || result.hazardProfileId !== hazardId || !result.passed) return;
    completedPosted.current = true;
    postToParent(embed, parentOrigin, {
      type: 'bozp:completed',
      hazardProfileId: result.hazardProfileId,
      score: result.score,
    });
  }, [result, hazardId, embed, parentOrigin]);

  if (!result || result.hazardProfileId !== hazardId) return null;

  const passed = result.passed;

  return (
    <div className="flex flex-1 flex-col">
      <Masthead />
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col items-center px-6 pb-16 text-center">
        <div className="hb-rise mt-4 flex flex-col items-center gap-5">
          <span
            aria-hidden
            className={`flex h-16 w-16 items-center justify-center rounded-full text-3xl ring-1 ${
              passed
                ? 'bg-[#edf6ee] text-[#2c6b39] ring-[#c4e2c9]'
                : 'bg-fixit-50 text-fixit-700 ring-fixit-200'
            }`}
          >
            {passed ? '✓' : '!'}
          </span>

          <div>
            {training?.title && (
              <p className="hb-eyebrow hb-eyebrow--accent mb-2">{training.title}</p>
            )}
            <h1 className="hb-title text-4xl">{passed ? t('You passed') : t('Not passed yet')}</h1>
            <p className="mx-auto mt-3 max-w-sm text-ink-soft">
              {passed
                ? t('Well done, this training is complete.')
                : t('Review the chapters and try the test again.')}
            </p>
          </div>

          <p className="font-display text-6xl font-medium tabular-nums text-ink-strong">
            {result.score}
            <span className="align-top text-2xl text-ink-faint">%</span>
          </p>
        </div>

        {passed ? (
          <div className="mt-10 flex w-full flex-col items-center gap-3">
            {showHubButton ? (
              <button
                type="button"
                data-testid="result-back-hub"
                onClick={() => navigate({ to: '/hub' })}
                className="hb-btn hb-btn--primary w-full sm:w-auto"
              >
                {t('Back to overview')}
              </button>
            ) : null}
            <ReturnButton
              returnUrl={returnUrl}
              embed={embed}
              parentOrigin={parentOrigin}
              variant={showHubButton ? 'ghost' : 'primary'}
            />
          </div>
        ) : (
          <div className="mt-9 w-full">
            {result.weakChapterIds.length > 0 && (
              <section className="mb-7 rounded-[var(--hb-radius)] border border-line bg-card p-5 text-left">
                <p className="hb-eyebrow mb-3">{t('Areas to review')}</p>
                <ul className="flex flex-col gap-2">
                  {result.weakChapterIds.map((id) => (
                    <li key={id} className="flex items-start gap-2.5 text-ink">
                      <span aria-hidden className="mt-0.5 text-fixit-600">
                        ●
                      </span>
                      <span>{titleFor(id)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                data-testid="result-review"
                onClick={() => navigate({ to: '/training/$hazardId', params: { hazardId } })}
                className="hb-btn hb-btn--ghost"
              >
                {t('Review chapters')}
              </button>
              <button
                type="button"
                data-testid="result-retake"
                onClick={() => navigate({ to: '/training/$hazardId/quiz', params: { hazardId } })}
                className="hb-btn hb-btn--primary"
              >
                {t('Retake the test')}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
